import type { ReactNode } from 'react';

/**
 * Surface card that wraps a wide `<table>` and lets it scroll horizontally on
 * narrow screens instead of squishing or clipping (docs/05-mobile-pwa-plan.md, A0).
 *
 * The outer card keeps `overflow-hidden` so the rounded corners clip the header
 * fill; the inner `overflow-x-auto` is the scroll viewport. For the scroll to
 * engage, the `<table>` inside needs an intrinsic `min-w-[Npx]` (it stays
 * `w-full` above that width) — set it on the table's own class, sized to the
 * column count. This replaces the hand-rolled
 * `bg-surface border ... rounded-token overflow-hidden` div that was
 * copy-pasted across the data pages.
 *
 * Shadow is intentionally NOT baked in — pages use slightly different card
 * elevations (the list pages a softer `0_10px_30px`, Assets a tighter
 * `0_4px_16px`), so pass it via `className` to preserve each page's look.
 *
 * steward-app#1807: the inner `overflow-x-auto` div carries `relative` so it
 * is the containing block for any `position: absolute` descendant with no
 * closer positioned ancestor (an `sr-only` header label is the case that
 * surfaced this — Tailwind's `.sr-only` is `position: absolute` with no
 * `top`/`left`/`right`). Without a positioned ancestor here, that descendant's
 * containing block became `<html>`, and its layout box (computed from the
 * *unclipped* table width, not the scrolled viewport) pushed
 * `document.documentElement.scrollWidth` past `document.body`'s, leaking a
 * page-level horizontal scroll from a table that was only ever supposed to
 * scroll inside this card. Confirmed via a `position: absolute|fixed` element
 * scan scoped to a `TableCard` instance (2026-09-25): the one hit was exactly
 * the `sr-only` "Actions" label, at the same x-coordinate as the leaked
 * `<html>` scrollWidth.
 */
export function TableCard({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div className={`bg-surface border border-line rounded-token overflow-hidden ${className}`}>
      <div className="relative overflow-x-auto">{children}</div>
    </div>
  );
}

const skelTh = 'text-left px-4 py-2.5 border-b border-line font-semibold text-slate text-[10px] uppercase tracking-[0.12em]';
const skelTd = 'px-4 py-3.5 border-b border-line/60';

/**
 * Loading placeholder for a `TableCard` list (Assets/Documents/Entities/
 * Liabilities). Renders the real header (columns are known statically, before
 * data arrives) plus shimmering bars standing in for row data, inside the same
 * `TableCard` shell the loaded table will use — so the header, card border, and
 * shadow are already in place and only the row content changes when the real
 * data lands, instead of a one-line "Loading…" text popping the whole table
 * into existence and shifting everything below it.
 *
 * The shimmer itself is the `.skeleton` utility (src/index.css) that
 * Dashboard's loading states already use — this is that same pattern applied
 * to table rows, not a second skeleton style.
 */
export function TableSkeleton({ columns, rows = 6, className = '' }: {
  /** Header labels, in column order (an empty string renders a blank header cell, e.g. for an actions column). */
  columns: string[];
  rows?: number;
  className?: string;
}) {
  return (
    <TableCard className={className}>
      <table className="w-full border-collapse">
        <thead className="bg-cream">
          <tr>
            {columns.map((label, i) => <th key={i} className={skelTh}>{label}</th>)}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, r) => (
            <tr key={r}>
              {columns.map((_, c) => (
                <td key={c} className={skelTd}>
                  <div className="skeleton h-4" style={{ width: c === 0 ? '70%' : '45%' }} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </TableCard>
  );
}
